import { device } from '@accedo/xdk-core'
import { ID as WEBOS } from '@accedo/xdk-device-lg-webos'
import { ID as TIZEN } from '@accedo/xdk-device-samsung-tizen'
import { ID as VIZIO } from '@accedo/xdk-device-vizio-smartcast-contrib'
import { Settings, Storage } from '@lightningjs/sdk'

import { TvBrowseLayoutModel } from '../types/flo-experience'
import { TizenSettings } from './deviceSettings/tizen'
import { VizioSettings } from './deviceSettings/vizio'
import { WebosSettings } from './deviceSettings/webos'
import { WorkstationSettings } from './deviceSettings/workstation'

const toNumber = (v: string): number | null => {
  const number = Number(v).valueOf()
  return Number.isNaN(number) ? null : number
}

const toDate = (v: string): Date => new Date(toNumber(v))

const APP_STARTED_AT = 'appStartedAt'
const APP_STARTED_COUNT = 'appStartedCount'
const INACTIVITY_TIMESTAMP = 'inactivityTimestamp'
const LIVE_SCORES_ENABLED = 'liveScoresEnabled'

type Env = {
  // Flag to read tts status, devs can enable this to debug tts locally
  APP_ENABLE_TTS?: boolean
  // Flag to get mouse support status, devs, can enable this to enable mouse capabilities locally
  APP_ENABLE_POINTER?: boolean
}

export class AppSettings {
  private _dictionary: object
  private _isCcOn: boolean
  private _device: DeviceSettings
  private _preFetchedHomeData: TvBrowseLayoutModel
  private _isCursorVisible: boolean = false

  public async init() {
    const platform = device.platform

    if (platform === TIZEN) {
      this._device = new TizenSettings()
    } else if (platform === WEBOS) {
      this._device = new WebosSettings()
    } else if (platform === VIZIO) {
      this._device = new VizioSettings()
    } else {
      this._device = new WorkstationSettings()
    }

    this._device?.init()
    // TODO: We don't have CC at this time so commenting this for now as it  makes vizio builds fail
    // because the code is trying to access Vizio APIs before they are loaded
    //this._isCcOn = (await this._device?.getCaptionState()) ?? false
    this.setLiveScoresFlag()
  }

  public set dictionary(v: object) {
    this._dictionary = v
  }

  public get dictionary() {
    return this._dictionary
  }

  public get isCcOn() {
    return this._isCcOn
  }

  public set isCcOn(v: boolean) {
    this._isCcOn = v
  }

  public get device() {
    return this._device
  }

  public get isMouseSupported() {
    const platform = device.platform

    return (Settings.get('app', 'env.APP_ENABLE_POINTER') || platform === WEBOS) ?? false
  }

  public set homeData(v: TvBrowseLayoutModel) {
    this._preFetchedHomeData = v
  }

  public get homeData() {
    return this._preFetchedHomeData
  }

  public getAppStartedAt() {
    return toDate(Storage.get(APP_STARTED_AT))
  }

  public getAppStartedCount() {
    return toNumber(Storage.get(APP_STARTED_COUNT)) ?? 0
  }

  public trackAppStart() {
    const count = this.getAppStartedCount()

    Storage.set(APP_STARTED_AT, Date.now().toString())
    Storage.set(APP_STARTED_COUNT, (count + 1).toString())
  }

  public setInactivityTimestamp() {
    Storage.set(INACTIVITY_TIMESTAMP, Date.now().toString())
  }

  public getInactivityTimestamp() {
    return toNumber(Storage.get(INACTIVITY_TIMESTAMP))
  }

  public setLiveScoresFlag() {
    if (!Storage.get(LIVE_SCORES_ENABLED)) {
      Storage.set(LIVE_SCORES_ENABLED, true)
    }
  }

  public toggleLiveScores(toggle: boolean) {
    Storage.set(LIVE_SCORES_ENABLED, toggle)
  }

  public areLiveScoresEnabled() {
    return Storage.get(LIVE_SCORES_ENABLED) ?? true
  }

  public get env(): Env {
    return Settings.get('app', 'env', {})
  }

  public get isCursorVisible() {
    return this._isCursorVisible
  }

  public set isCursorVisible(isVisible: boolean) {
    this._isCursorVisible = isVisible
  }

  public get isMouseSupportEnabled() {
    return Settings.get('app', 'enablePointer', false)
  }
}
